/**
 * Normalize / dedupe legacy question responses.
 * Going through the Questions flow more than once used to APPEND answers,
 * so the array could grow to 20+ entries for a 10-question template.
 */

export function questionDedupeKey(questionText = '') {
  return String(questionText).trim().toLowerCase().replace(/\s+/g, ' ');
}

function hasMediaBlob(q) {
  return (q?.videoBlob instanceof Blob && q.videoBlob.size > 0)
    || (q?.audioBlob instanceof Blob && q.audioBlob.size > 0);
}

function mediaSize(q) {
  const b = q?.videoBlob || q?.audioBlob;
  return b instanceof Blob ? b.size : 0;
}

function timestampMs(q) {
  const t = Date.parse(q?.timestamp || '');
  return Number.isFinite(t) ? t : 0;
}

/**
 * Keep one response per question text. Prefer entries that still have a Blob
 * (after refresh stubs lose blobs). Among equals, prefer newest / largest.
 */
export function dedupeQuestionResponses(questions = []) {
  if (!Array.isArray(questions) || questions.length <= 1) {
    return Array.isArray(questions) ? questions : [];
  }

  const byKey = new Map();
  const order = [];

  for (const q of questions) {
    if (!q || typeof q !== 'object') continue;
    const key = questionDedupeKey(q.question);
    // Fall back to position-less unique key for empty text (shouldn't happen)
    const mapKey = key || `__empty_${order.length}`;

    if (!byKey.has(mapKey)) {
      byKey.set(mapKey, q);
      order.push(mapKey);
      continue;
    }

    const prev = byKey.get(mapKey);
    const prevHas = hasMediaBlob(prev);
    const nextHas = hasMediaBlob(q);

    let winner = prev;
    if (nextHas && !prevHas) {
      winner = q;
    } else if (nextHas && prevHas) {
      const newer = timestampMs(q) >= timestampMs(prev);
      const bigger = mediaSize(q) > mediaSize(prev);
      winner = (newer || bigger) ? q : prev;
    } else if (!nextHas && !prevHas) {
      winner = timestampMs(q) >= timestampMs(prev) ? q : prev;
    }

    byKey.set(mapKey, winner);
  }

  return order.map((k) => byKey.get(k)).filter(Boolean);
}

export function countValidMediaClips(questions = []) {
  return questions.filter(hasMediaBlob).length;
}
